import React, { useState } from "react";
import { kitchenService } from "../../services";
import upload from "../../assets/images/registration/upload.png";
import { toast } from 'react-toastify';
import { uploadMultipleImages } from "../../utils/imageUploader";

interface Form8Props {
  onNext: (data: Form8Data) => void;
  initialData?: Form8Data;
}

export interface Form8Data {
  kitchenPhotos: string[];
  menuCardImages: string[];
  foodItemImages: string[];
}

const Form8: React.FC<Form8Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<Form8Data>({
    kitchenPhotos: initialData?.kitchenPhotos || [],
    menuCardImages: initialData?.menuCardImages || [],
    foodItemImages: initialData?.foodItemImages || [],
  });

  console.log("formData", formData);

  const [loading, setLoading] = useState(false);
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [error, setError] = useState("");

  const handleFileUpload = async (
    field: keyof Form8Data,
    files: FileList | null
  ) => {
    if (!files || files.length === 0) return;

    setUploadingField(field);
    setError("");

    try {
      const uploadedUrls = await uploadMultipleImages(
        Array.from(files), 
        'gallery', 
        // { maxSize: 5 }
      );
      
      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], ...uploadedUrls],
      }));
      
      toast.success(`${uploadedUrls.length} image(s) uploaded successfully!`);
    } catch (err: any) {
      setError(err.message || 'Failed to upload images');
      toast.error(err.message || 'Failed to upload images');
    } finally {
      setUploadingField(null);
    }
  };

  const handleRemoveImage = (field: keyof Form8Data, index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    // if (formData.kitchenPhotos.length === 0) {
    //   setError("Please upload at least one kitchen photo");
    //   return;
    // }
    // if (formData.menuCardImages.length === 0) {
    //   setError("Please upload at least one menu card image");
    //   return;
    // }
    // if (formData.foodItemImages.length === 0) {
    //   setError("Please upload at least one food item image");
    //   return;
    // }

    setLoading(true);
    try {
      // Submit to API
      const response: any = await kitchenService.submitStep8(formData);
      toast.success(response.message || 'Media uploaded successfully! Registration complete!');
      // Move to next step (completion)
      onNext(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save data. Please try again.');
      toast.error(err.message || 'Failed to upload media');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="w-full">
        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Photos & Media
        </h2>
        <p className="text-[#6A6A6A] text-[15px] font-medium leading-[140%]">
          Enter these details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* Kitchen/Restaurant Photo */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="kitchenPhotos"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Kitchen/Restaurant Photo
            </label>
            <div className="relative">
              <input
                type="file"
                id="kitchenPhotos"
                multiple
                accept="image/*"
                onChange={(e) => handleFileUpload("kitchenPhotos", e.target.files)}
                className="hidden"
                disabled={uploadingField === "kitchenPhotos"}
              />
              <label
                htmlFor="kitchenPhotos"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 hover:bg-white cursor-pointer transition-all flex items-center justify-between"
              >
                <span className="text-gray-500">
                  {uploadingField === "kitchenPhotos" 
                    ? "Uploading..." 
                    : "Click to upload images"}
                </span>
                <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
              </label>
            </div>
            {formData.kitchenPhotos.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-gray-600 mb-2">
                  {formData.kitchenPhotos.length} image(s) uploaded
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {formData.kitchenPhotos.map((url, index) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={`Kitchen ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage("kitchenPhotos", index)}
                        className="absolute -top-[4px] -right-[4px] bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Menu Card Upload */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="menuCardImages"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Menu Card Upload
            </label>
            <div className="relative">
              <input
                type="file"
                id="menuCardImages"
                multiple
                accept="image/*"
                onChange={(e) => handleFileUpload("menuCardImages", e.target.files)}
                className="hidden"
                disabled={uploadingField === "menuCardImages"}
              />
              <label
                htmlFor="menuCardImages"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 hover:bg-white cursor-pointer transition-all flex items-center justify-between"
              >
                <span className="text-gray-500">
                  {uploadingField === "menuCardImages" 
                    ? "Uploading..." 
                    : "Click to upload menu cards"}
                </span>
                <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
              </label>
            </div>
            {formData.menuCardImages.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-gray-600 mb-2">
                  {formData.menuCardImages.length} image(s) uploaded
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {formData.menuCardImages.map((url, index) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={`Menu ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage("menuCardImages", index)}
                        className="absolute -top-[4px] -right-[4px] bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Food Item Photos */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="foodItemImages"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Food Item Photos
            </label>
            <div className="relative">
              <input
                type="file"
                id="foodItemImages"
                multiple
                accept="image/*"
                onChange={(e) => handleFileUpload("foodItemImages", e.target.files)}
                className="hidden"
                disabled={uploadingField === "foodItemImages"}
              />
              <label
                htmlFor="foodItemImages"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 hover:bg-white cursor-pointer transition-all flex items-center justify-between"
              >
                <span className="text-gray-500">
                  {uploadingField === "foodItemImages" 
                    ? "Uploading..." 
                    : "Click to upload food images"}
                </span>
                <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
              </label>
            </div>
            {formData.foodItemImages.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-gray-600 mb-2">
                  {formData.foodItemImages.length} image(s) uploaded
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {formData.foodItemImages.map((url, index) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={`Food ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage("foodItemImages", index)}
                        className="absolute -top-[4px] -right-[4px] bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="w-full px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form8;
